import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { format, addDays } from 'date-fns';
import { User, Mail, Phone, MessageCircle, Send, Calendar, FileEdit, PlayCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { useIframeCommunication } from '../../hooks/useIframeCommunication';
import { useConfig } from '../../contexts/ConfigContext';
import { getSpecializationTerm, getSpecializationPrefix } from '../../utils/organizationHelpers';
import BookingWidget from '../../components/BookingWidget';
import Footer from '../../components/Footer';
import { formatDateLongRu } from '../../utils/dateUtils';
import { formatUserDisplayName } from '../../utils/userDisplayName';
import { therapistPublicShort, therapistPublicFull } from '../../utils/therapistPublicText';
import NaslediePublicProfileLanding from './NaslediePublicProfileLanding';
import './PublicProfilePage.css';

const PublicProfilePage = () => {
    // Общение с родительским окном через postMessage
    useIframeCommunication();
    const { id } = useParams();
    const { config } = useConfig();
    const orgCode = config?.code || 'mag';
    const [showQuestionForm, setShowQuestionForm] = useState(false);
    const [showBookingForm, setShowBookingForm] = useState(false);
    const [showVideoCard, setShowVideoCard] = useState(false);
    const [bookingSuccess, setBookingSuccess] = useState(null);
    const [selectedDirection, setSelectedDirection] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(null);

    const [formData, setFormData] = useState({
        sender_name: '',
        sender_email: '',
        sender_phone: '',
        message: ''
    });

    // Загрузка профиля пользователя
    const { data: profile, isLoading } = useQuery(
        ['publicProfile', id],
        async () => {
            const response = await axios.get(`${API_BASE_URL}/users/public/${id}`);
            return response.data;
        },
        {
            retry: 1,
            refetchOnWindowFocus: false
        }
    );

    // Загрузка доступных слотов для психолога (на 2 недели вперед)
    const startDate = format(new Date(), 'yyyy-MM-dd');
    const endDate = format(addDays(new Date(), 14), 'yyyy-MM-dd');

    const { data: availableSlots } = useQuery(
        ['availableSlots', id, startDate, endDate],
        async () => {
            if (!profile?.is_therapist) return null;

            const response = await axios.get(`${API_BASE_URL}/therapist/${id}/available-slots`, {
                params: { start_date: startDate, end_date: endDate }
            });
            return response.data;
        },
        {
            enabled: !!profile?.is_therapist,
            retry: 1
        }
    );

    // Загрузка статей пользователя
    const { data: userArticles } = useQuery(
        ['userArticles', id],
        async () => {
            const response = await axios.get(`${API_BASE_URL}/users/${id}/articles`);
            return response.data;
        },
        {
            enabled: !!id,
            retry: 1
        }
    );

    // Загрузка групп психолога (только для Наследия)
    const { data: allGroupsRaw, isLoading: groupsLoading } = useQuery(
        ['therapistGroups', id],
        async () => {
            const response = await axios.get(`${API_BASE_URL}/users/${id}/teacher-groups`);
            return response.data;
        },
        {
            enabled: !!id && orgCode === 'nasledie',
            retry: 1
        }
    );

    // Фильтруем группы: open/started_additional; с расписанием или с форматом "регулярно"
    const allGroups = useMemo(() => {
        if (!allGroupsRaw) return [];
        return allGroupsRaw.filter(group => {
            const status = group.recruitment_status;
            if (status !== 'open' && status !== 'started_additional') return false;
            const hasRegularFormat = group.format_data?.[0]?.frequency === 'weekly' || group.format_data?.[0]?.frequency === 'biweekly';
            return group.earliest_event_date || hasRegularFormat;
        });
    }, [allGroupsRaw]);

    // Загрузка направлений для фильтров групп
    const { data: directions } = useQuery('publicDirections', () =>
        axios.get(`${API_BASE_URL}/directions/public`).then(res => res.data),
        { enabled: orgCode === 'nasledie' }
    );

    // Группируем направления - объединяем все "Специализация: *" / "Авторская программа: *" в одно
    const displayDirections = useMemo(() => {
        if (!directions || !allGroups) return [];

        const prefix = getSpecializationPrefix(orgCode);
        const specializationTerm = getSpecializationTerm(orgCode);

        // Получаем уникальные ID направлений, для которых есть группы
        const availableDirectionIds = new Set(allGroups.map(g => g.direction_id).filter(Boolean));

        // Фильтруем только доступные направления
        const availableDirections = directions.filter(d => availableDirectionIds.has(d.id));

        const specializations = availableDirections.filter(d => d.name.startsWith(prefix));
        const otherDirections = availableDirections.filter(d => !d.name.startsWith(prefix));

        const result = [];

        // Добавляем объединенную кнопку "Специализация" / "Авторская программа", если есть такие направления
        if (specializations.length > 0) {
            result.push({
                id: 'specialization',
                name: specializationTerm,
                directionIds: specializations.map(s => s.id)
            });
        }

        // Добавляем остальные направления
        otherDirections.forEach(d => {
            result.push({
                id: d.id,
                name: d.name,
                directionIds: [d.id]
            });
        });

        return result;
    }, [directions, allGroups, orgCode]);

    // Формируем список ID направлений для фильтрации
    const selectedDirectionIds = useMemo(() => {
        if (!selectedDirection) return null;
        const direction = displayDirections.find(d => d.id === selectedDirection);
        return direction ? direction.directionIds : null;
    }, [selectedDirection, displayDirections]);

    // Фильтрация групп на клиенте (как на /public/groups)
    const groups = useMemo(() => {
        if (!allGroups) return [];

        const filtered = allGroups.filter(group => {
            if (selectedDirectionIds && !selectedDirectionIds.includes(group.direction_id)) {
                return false;
            }

            if (selectedRegion) {
                const regionName = group.region_name || '';
                const hasMoscow = regionName.includes('Москва');
                const hasOnline = regionName.includes('Онлайн');

                if (selectedRegion === 'Москва') {
                    if (!hasMoscow) return false;
                } else if (selectedRegion === 'Онлайн') {
                    if (!hasOnline) return false;
                } else if (selectedRegion === 'Регионы') {
                    if (hasMoscow || hasOnline) return false;
                }
            }

            return true;
        });

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const getKey = (g) => {
            if (!g.earliest_event_date) {
                return { bucket: 1, time: Number.MAX_SAFE_INTEGER };
            }
            const d = new Date(g.earliest_event_date);
            d.setHours(0, 0, 0, 0);
            const ts = d.getTime();
            if (ts >= today.getTime()) return { bucket: 0, time: ts };
            return { bucket: 2, time: ts };
        };

        return [...filtered].sort((a, b) => {
            const ka = getKey(a);
            const kb = getKey(b);
            if (ka.bucket !== kb.bucket) return ka.bucket - kb.bucket;
            if (ka.bucket === 0) return ka.time - kb.time;
            if (ka.bucket === 2) return kb.time - ka.time;
            return 0;
        });
    }, [allGroups, selectedDirectionIds, selectedRegion]);

    const handleDirectionClick = (directionId) => {
        setSelectedDirection((prev) => (prev === directionId ? null : directionId));
    };

    const handleRegionClick = (regionKey) => {
        setSelectedRegion((prev) => (prev === regionKey ? null : regionKey));
    };

    // Отправка вопроса
    const sendQuestionMutation = useMutation(
        async (data) => {
            await axios.post(`${API_BASE_URL}/users/send-question`, data);
        },
        {
            onSuccess: () => {
                toast.success('Вопрос успешно отправлен!');
                setShowQuestionForm(false);
                setFormData({
                    sender_name: '',
                    sender_email: '',
                    sender_phone: '',
                    message: ''
                });
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Ошибка отправки вопроса');
            }
        }
    );

    // Создание записи на консультацию
    const createBookingMutation = useMutation(
        async (data) => {
            const response = await axios.post(`${API_BASE_URL}/therapist/booking`, {
                therapist_id: parseInt(id),
                ...data
            });
            return { ...data, ...response.data };
        },
        {
            onSuccess: (data) => {
                setBookingSuccess(data);
                setTimeout(() => {
                    setShowBookingForm(false);
                    setBookingSuccess(null);
                }, 5000);
            },
            onError: (error) => {
                toast.error(error.response?.data?.message || 'Ошибка создания записи');
            }
        }
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.sender_name || !formData.sender_email || !formData.message) {
            toast.error('Заполните все обязательные поля');
            return;
        }

        sendQuestionMutation.mutate({
            user_id: parseInt(id),
            ...formData
        });
    };

    const handleBooking = (bookingData) => {
        createBookingMutation.mutate(bookingData);
    };

    if (isLoading) {
        return (
            <div className="public-profile-page">
                <div className="loading-container">
                    <div className="loading">
                        <div className="spinner"></div>
                        <p>Загрузка профиля...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="public-profile-page">
                <div className="error-container">
                    <h2>Пользователь не найден</h2>
                    <p>Профиль не существует или был удален</p>
                </div>
            </div>
        );
    }

    // Проверка доступа к профилю
    if (!profile.is_therapist && !profile.is_teacher) {
        return (
            <div className="public-profile-page">
                <div className="error-container">
                    <h2>Профиль недоступен</h2>
                    <p>Этот профиль не является публичным</p>
                </div>
            </div>
        );
    }

    const qualifications = Array.isArray(profile.qualification)
        ? profile.qualification
        : (profile.qualification ? [profile.qualification] : []);

    const headerShort = profile.is_therapist ? therapistPublicShort(profile) : (profile.short_description || '');
    const aboutText = profile.is_therapist ? therapistPublicFull(profile) : (profile.description || '');
    const educationText =
        profile.is_therapist && profile.therapist_education ? String(profile.therapist_education).trim() : '';
    const showAboutSection =
        Boolean(aboutText?.trim()) ||
        (profile.is_therapist && Boolean(educationText));

    const sharedModals = (
        <>
            {/* Модальное окно записи на консультацию */}
            {showBookingForm && (
                <div className="booking-modal-overlay" onClick={() => !bookingSuccess && setShowBookingForm(false)}>
                    <div className="booking-modal-content" onClick={(e) => e.stopPropagation()}>
                        {bookingSuccess ? (
                            <div className="booking-success">
                                <div className="success-icon">✓</div>
                                <h2>Запись успешно создана!</h2>
                                <div className="success-details">
                                    <p><strong>Дата:</strong> {formatDateLongRu(bookingSuccess.booking_date)}</p>
                                    <p><strong>Время:</strong> {bookingSuccess.start_time.slice(0, 5)} - {bookingSuccess.end_time.slice(0, 5)}</p>
                                    <p><strong>Психолог:</strong> {formatUserDisplayName(profile)}</p>
                                </div>
                                <p className="success-message">
                                    Мы отправили уведомление психологу. Он свяжется с вами в ближайшее время для подтверждения записи.
                                </p>
                                <button
                                    className="btn btn-primary"
                                    onClick={() => {
                                        setShowBookingForm(false);
                                        setBookingSuccess(null);
                                    }}
                                >
                                    Закрыть
                                </button>
                            </div>
                        ) : (
                            <BookingWidget
                                therapistName={formatUserDisplayName(profile)}
                                price={profile.price}
                                topic_prices={profile.topic_prices}
                                therapy_topics={profile.therapy_topics}
                                availableSlots={availableSlots}
                                onBook={handleBooking}
                                onCancel={() => setShowBookingForm(false)}
                                loading={createBookingMutation.isLoading}
                            />
                        )}
                    </div>
                </div>
            )}

            {/* Модальное окно видеовизитки */}
            {showVideoCard && profile.video_card_url && (
                <div className="booking-modal-overlay" onClick={() => setShowVideoCard(false)}>
                    <div className="booking-modal-content video-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                <PlayCircle size={24} />
                                Видеовизитка
                            </h2>
                            <button className="modal-close" onClick={() => setShowVideoCard(false)}>
                                ×
                            </button>
                        </div>
                        <div className="video-container">
                            {profile.video_card_url.includes('youtube.com') || profile.video_card_url.includes('youtu.be') ? (
                                <iframe
                                    src={profile.video_card_url.replace('watch?v=', 'embed/')}
                                    title="Видеовизитка"
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            ) : (
                                <a href={profile.video_card_url} target="_blank" rel="noopener noreferrer" className="video-link">
                                    Открыть видеовизитку
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Модальное окно формы вопроса */}
            {showQuestionForm && (
                <div className="booking-modal-overlay" onClick={() => setShowQuestionForm(false)}>
                    <div className="booking-modal-content question-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                <Mail size={24} />
                                Задать вопрос
                            </h2>
                            <button className="modal-close" onClick={() => setShowQuestionForm(false)}>
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="question-form">
                            <div className="form-group">
                                <label>Ваше имя *</label>
                                <input
                                    type="text"
                                    name="sender_name"
                                    value={formData.sender_name}
                                    onChange={handleInputChange}
                                    placeholder="Введите ваше имя"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Email *</label>
                                <input
                                    type="email"
                                    name="sender_email"
                                    value={formData.sender_email}
                                    onChange={handleInputChange}
                                    placeholder="your@email.com"
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Телефон</label>
                                <input
                                    type="tel"
                                    name="sender_phone"
                                    value={formData.sender_phone}
                                    onChange={handleInputChange}
                                    placeholder="+7 (999) 999-99-99"
                                />
                            </div>

                            <div className="form-group">
                                <label>Ваш вопрос *</label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    placeholder="Напишите ваш вопрос..."
                                    rows="6"
                                    required
                                />
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowQuestionForm(false)}
                                    disabled={sendQuestionMutation.isLoading}
                                >
                                    Отмена
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={sendQuestionMutation.isLoading}
                                >
                                    <Send size={18} />
                                    {sendQuestionMutation.isLoading ? 'Отправка...' : 'Отправить'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            <Footer />
        </>
    );

    if (orgCode === 'nasledie') {
        return (
            <div className="public-profile-page public-profile-page--nasledie">
                <NaslediePublicProfileLanding
                    profile={profile}
                    headerShort={headerShort}
                    aboutText={aboutText}
                    educationText={educationText}
                    qualifications={qualifications}
                    userArticles={userArticles}
                    allGroups={allGroups}
                    groups={groups}
                    displayDirections={displayDirections}
                    selectedDirection={selectedDirection}
                    selectedRegion={selectedRegion}
                    onDirectionClick={handleDirectionClick}
                    onRegionClick={handleRegionClick}
                    onBook={() => setShowBookingForm(true)}
                    onAsk={() => setShowQuestionForm(true)}
                    onOpenVideo={() => {
                        if (profile.video_card_url) window.open(profile.video_card_url, '_blank');
                    }}
                />
                {sharedModals}
            </div>
        );
    }

    return (
        <div className="public-profile-page">
            <div className="profile-container">
                <div className="profile-header">
                    <div className="profile-header-content">
                        {profile.photo_url ? (
                            <img src={profile.photo_url} alt={formatUserDisplayName(profile)} className="profile-photo" />
                        ) : (
                            <div className="profile-photo-placeholder">
                                <User size={48} />
                            </div>
                        )}
                        <div className="profile-header-info">
                            <h1>{formatUserDisplayName(profile)}</h1>
                            {headerShort && (
                                <p className="profile-short-description">{headerShort}</p>
                            )}
                        </div>
                        {/* Кнопки действий */}
                        <div className="profile-header-actions">
                            {profile.is_therapist && (
                                <>
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => setShowBookingForm(true)}
                                    >
                                        <Calendar size={18} />
                                        Записаться на консультацию
                                    </button>
                                    {(userArticles && userArticles.length > 0) && (
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => {
                                                const articlesSection = document.getElementById('articles-section');
                                                if (articlesSection) {
                                                    articlesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                                }
                                            }}
                                        >
                                            <FileEdit size={18} />
                                            Статьи
                                        </button>
                                    )}
                                    {profile.video_card_url && (
                                        <button
                                            className="btn btn-secondary"
                                            onClick={() => {

                                                window.open(profile.video_card_url, '_blank');
                                            }}
                                        >
                                            <PlayCircle size={18} />
                                            Видеовизитка
                                        </button>
                                    )}
                                </>
                            )}
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowQuestionForm(true)}
                            >
                                <MessageCircle size={18} />
                                Задать вопрос
                            </button>
                        </div>
                    </div>
                </div>

                <div className="profile-body">
                    {/* Информация психолога - компактный блок */}
                    {profile.is_therapist && (
                        <div className="profile-section therapist-info-compact">
                            {qualifications.length > 0 && (
                                <div className="info-row">
                                    <span className="info-label">Квалификация:</span>
                                    <div className="tags-inline">
                                        {qualifications.map((q, idx) => (
                                            <span key={idx} className="tag tag-sm">{q}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {(profile.city && profile.city.length > 0) && (
                                <div className="info-row">
                                    <span className="info-label">Города:</span>
                                    <div className="tags-inline">
                                        {profile.city.map((city, idx) => (
                                            <span key={idx} className="tag tag-sm">{city}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(profile.language && profile.language.length > 0) && (
                                <div className="info-row">
                                    <span className="info-label">Языки:</span>
                                    <div className="tags-inline">
                                        {profile.language.map((lang, idx) => (
                                            <span key={idx} className="tag tag-sm">{lang}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(profile.work_format && profile.work_format.length > 0) && (
                                <div className="info-row">
                                    <span className="info-label">Форматы:</span>
                                    <div className="tags-inline">
                                        {profile.work_format.map((format, idx) => (
                                            <span key={idx} className="tag tag-sm">{format}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(profile.therapy_topics && profile.therapy_topics.length > 0) && (
                                <div className="info-row info-row--stack">
                                    <span className="info-label">Темы-запросы:</span>
                                    <div className="tags-inline">
                                        {profile.therapy_topics.map((topic, idx) => (
                                            <span key={idx} className="tag tag-sm tag-topic">{topic}</span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Цены */}
                            {(profile.price || (profile.topic_prices && Object.keys(profile.topic_prices).length > 0)) && (
                                <div className="info-row prices-row">
                                    <span className="info-label">Стоимость консультации:</span>
                                    <div className="prices-list">
                                        {profile.price && (
                                            <div className="price-item">
                                                <span className="price-label">Базовая цена:</span>
                                                <span className="price-value">
                                                    {profile.price} ₽
                                                </span>
                                            </div>
                                        )}
                                        {profile.topic_prices && Object.keys(profile.topic_prices).length > 0 && (
                                            <>
                                                {Object.entries(profile.topic_prices).map(([topic, price]) => (
                                                    <div key={topic} className="price-item">
                                                        <span className="price-label">{topic}:</span>
                                                        <span className="price-value">
                                                            {price} ₽
                                                        </span>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {showAboutSection && (
                        <div className="profile-section">
                            <h2>О себе</h2>
                            {aboutText?.trim() ? (
                                <div className="profile-description">{aboutText}</div>
                            ) : null}
                            {educationText ? (
                                <>
                                    <h3 className="profile-education-heading">Образование</h3>
                                    <div className="profile-description profile-education">{educationText}</div>
                                </>
                            ) : null}

                            {profile.video_card_url && (
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => {

                                        window.open(profile.video_card_url, '_blank');
                                    }}
                                >
                                    <PlayCircle size={18} />
                                    Видеовизитка
                                </button>
                            )}
                        </div>
                    )}


                    {profile.certificates && profile.certificates.length > 0 && (
                        <div className="profile-section certificates-section">
                            <h2>Сертификаты</h2>
                            <div className="certificates-gallery">
                                {profile.certificates.map((cert, index) => (
                                    <div key={index} className="certificate-gallery-item">
                                        <img
                                            src={cert.startsWith('/uploads/')
                                                ? `${window.location.origin}${cert}`
                                                : cert}
                                            alt={`Сертификат ${index + 1}`}
                                            onClick={() => {
                                                const url = cert.startsWith('/uploads/')
                                                    ? `${window.location.origin}${cert}`
                                                    : cert;
                                                window.open(url, '_blank');
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {userArticles && userArticles.length > 0 && (
                        <div id="articles-section" className="profile-section articles-section">
                            <h2>
                                <FileEdit size={24} />
                                Статьи
                            </h2>
                            <div className="profile-articles-grid">
                                {userArticles.map(article => (
                                    <Link
                                        key={article.id}
                                        to={`/public/articles/${article.id}`}
                                        className="profile-article-card"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {article.cover_image_url && (
                                            <div className="profile-article-image">
                                                <img src={article.cover_image_url} alt={article.title} />
                                            </div>
                                        )}
                                        <div className="profile-article-content">
                                            <h3 className="profile-article-title">{article.title}</h3>
                                            {article.excerpt && (
                                                <p className="profile-article-excerpt">{article.excerpt}</p>
                                            )}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {sharedModals}
        </div>
    );
};

export default PublicProfilePage;

